<template>
  <a-modal
    class="modal-fullscreen"
    :class="{ 'modal-fullscreen--expanded': isFullscreen }"
    :visible="visible"
    :width="isFullscreen ? '100vw' : normalizedWidth"
    :wrap-class-name="mergedWrapClassName"
    v-bind="$attrs"
    :closable="false"
    @cancel="handleCancel"
    @ok="handleOk"
  >
    <template slot="title">
      <div class="modal-fullscreen__title">
        <div class="modal-fullscreen__title-content">
          <slot name="title" :isFullscreen="isFullscreen" :toggleFullscreen="toggleFullscreen">
            {{ title }}
          </slot>
        </div>

        <div class="flex gap-2 align-items-center">
          <a-tooltip v-if="fullscreenEnabled">
            <template slot="title">
              {{ isFullscreen ? exitFullscreenText : enterFullscreenText }}
            </template>
            <span @click="toggleFullscreen" class="action-button">
              <custom-icon
                width="20"
                height="20"
                :icon="
                  isFullscreen ? 'material-symbols:close-fullscreen-rounded' : 'material-symbols:fullscreen-rounded'
                "
              />
            </span>
          </a-tooltip>

          <a-tooltip>
            <template slot="title">Close</template>
            <span @click="handleCancel" class="action-button">
              <custom-icon width="20" height="20" icon="material-symbols:close-rounded" />
            </span>
          </a-tooltip>
        </div>
      </div>
    </template>

    <slot :isFullscreen="isFullscreen" :toggleFullscreen="toggleFullscreen" />

    <template v-if="$slots.footer" slot="footer">
      <slot name="footer" :isFullscreen="isFullscreen" :toggleFullscreen="toggleFullscreen" />
    </template>
  </a-modal>
</template>

<script>
import { Modal, Tooltip } from 'ant-design-vue'
import AppIcon from '@/components/ui/app-icon.vue'

export default {
  name: 'ModalFullscreen',
  components: {
    'a-modal': Modal,
    'a-tooltip': Tooltip,
    'custom-icon': AppIcon
  },
  inheritAttrs: false,
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    width: {
      type: [String, Number],
      default: '520px'
    },
    wrapClassName: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    fullscreenEnabled: {
      type: Boolean,
      default: true
    },
    resetOnClose: {
      type: Boolean,
      default: true
    },
    enterFullscreenText: {
      type: String,
      default: 'Open Full View'
    },
    exitFullscreenText: {
      type: String,
      default: 'Exit Full View'
    }
  },
  data() {
    return {
      isFullscreen: false
    }
  },
  computed: {
    normalizedWidth() {
      if (typeof this.width === 'number') {
        return `${this.width}px`
      }

      return this.width
    },
    mergedWrapClassName() {
      const classes = ['modal-fullscreen-wrap']

      if (this.wrapClassName) {
        classes.push(this.wrapClassName)
      }

      if (this.isFullscreen) {
        classes.push('modal-fullscreen-wrap--expanded')
      }

      return classes.join(' ')
    }
  },
  watch: {
    visible(newVal) {
      if (!newVal && this.resetOnClose) {
        this.isFullscreen = false
      }
    }
  },
  methods: {
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
    },
    handleCancel(event) {
      if (this.resetOnClose) {
        this.isFullscreen = false
      }

      this.$emit('cancel', event)
    },
    handleOk(event) {
      this.$emit('ok', event)
    }
  }
}
</script>

<style scoped>
.modal-fullscreen__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.modal-fullscreen__title-content {
  min-width: 0;
}

.modal-fullscreen--expanded ::v-deep .ant-modal {
  width: 100vw !important;
  max-width: 100vw !important;
  top: 0 !important;
  margin: 0;
  padding-bottom: 0;
}

.modal-fullscreen--expanded ::v-deep .ant-modal-content {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.modal-fullscreen--expanded ::v-deep .ant-modal-body {
  flex: 1;
  overflow: auto;
}

.modal-fullscreen--expanded ::v-deep .ant-tabs-content {
  height: 100%;
}
</style>
